import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { PaymentIntentResult, StripeCardNumberElement } from '@stripe/stripe-js';
import { useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import { useMutation } from 'react-query';

import { getStripePaymentSecretKey } from '../../../api/payment/payment';
import {
  PaymentDTO,
  ProductRegistryItemT,
  SustainableItemsT,
} from '../../../api/payment/payment.d';
import { PAYMENT_ENV } from '../../../config';
import { useCommon } from '../../../context/CommonContext';
import { useLocale } from '../../../context/LocaleContext';
import { CalculatorTypeE } from '../../../declarations/enums/common';
import { CurrencyE } from '../../../declarations/enums/currency';
import { RoutesPathE } from '../../../declarations/enums/routes';
import { isNumeric } from '../../../helpers/numbers';
import { replaceRouteParams } from '../../../helpers/routes';
import useReactRouter from '../../../hooks/useReactRouter';
import { SelectedCarbonOffsetProjectT } from '../../CarbonOffsetsPage/hooks/useCarbonOffsets';
import { SelectedSustainableActionsT } from '../../SustainableActionsPage/hooks/useSustainableActions';
import { GuestInformationFormValueT } from '../components/GuestInformationForm/GuestInformationForm';
import { QueryT } from './usePaymentPage';

type QueryParamsT = {
  calculate?: CalculatorTypeE;
} & QueryT;

type FormFieldsT = 'cardNumber' | 'cardExpiry' | 'cvc';

type ErrorT = {
  [key in FormFieldsT]: {
    complete: boolean;
    errorMessage?: string;
  };
};

const initErrors: ErrorT = {
  cardNumber: {
    complete: false,
    errorMessage: '',
  },
  cardExpiry: { complete: false, errorMessage: '' },
  cvc: { complete: false, errorMessage: '' },
};

const usePaymentForm = (
  sustainableActions: SelectedSustainableActionsT[],
  carbonOffsets: SelectedCarbonOffsetProjectT[]
) => {
  const { customerId } = useCommon();
  const { getUrlQueryParams } = useReactRouter();
  const { selectedLocale } = useLocale();

  const { calculate } = getUrlQueryParams<QueryParamsT>();
  const stripe = useStripe();
  const elements = useElements();
  const { navigateTo } = useReactRouter();
  const { values } = useFormikContext<GuestInformationFormValueT>();
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
  const [errors, setErrors] = useState(initErrors);
  const [serverError, setServerError] = useState('');
  const [confirmationId, setConfirmationId] = useState('');

  const paymentKeyPayload = useMemo((): Omit<PaymentDTO, 'billingDetails'> => {
    let totalSustainablePrice = 0;
    let totalProjectPrice = 0;

    const productRegistryItem = carbonOffsets?.map(
      ({ id, selectedQty, basePrice, totalCharge }) => {
        totalProjectPrice += Number(totalCharge);

        return {
          projectId: id,
          tonnes: selectedQty,
          cost: Number(totalCharge),
          currency: CurrencyE.USD,
          pricePerTonne: basePrice,
        };
      }
    );

    const sustainableItemsList = sustainableActions?.map(
      ({ id, pricePerUnit, totalCharge, qty }) => {
        totalSustainablePrice += Number(totalCharge);

        return {
          sustainableItemId: id,
          pricePerUnit,
          priceUnit: pricePerUnit,
          quantity: qty,
          cost: Number(totalCharge),
          currency: CurrencyE.USD,
        };
      }
    );

    return {
      customerId,
      productRegistryItem: (productRegistryItem || []) as ProductRegistryItemT[],
      sustainableItems: (sustainableItemsList || []) as SustainableItemsT[],
      bookingDate: new Date(Date.now()).toDateString(),
      currency: CurrencyE.USD,
      environment: PAYMENT_ENV,
      hotelStay: calculate !== CalculatorTypeE.TRANSPORT,
      totalPaid: totalSustainablePrice + totalProjectPrice,
      totalSustainablePrice,
      totalProjectPrice,
      locale: selectedLocale,
    };
  }, [sustainableActions, carbonOffsets, calculate]);

  const { isLoading: isLoadingPaymentKey, mutate: getPaymentKey } = useMutation(
    (payload: PaymentDTO) => getStripePaymentSecretKey(payload),
    {
      onSuccess({ data }) {
        setConfirmationId(data.confirmationId);

        onSubmitPayment(data.clientSecret);
      },
    }
  );

  const { isLoading: isLoadingPayment, mutate: onSubmitPayment } = useMutation(
    (clientSecret: string) => {
      if (!stripe || !elements) {
        return {} as Promise<PaymentIntentResult>;
      }

      return stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement) as StripeCardNumberElement,
        },
      });
    },
    {
      onSuccess(res) {
        if (res.error) {
          setServerError(res.error.message as string);
          setIsConfirmPopupOpen(false);

          return;
        }

        const confirmationUrl = replaceRouteParams(RoutesPathE.CONFIRMATION_PAGE, {
          confirmationId,
        });
        navigateTo(confirmationUrl);
      },
    }
  );

  const handleParsePrice = (val: string | number) => {
    // parse appropriate values as BE expects
    if (isNumeric(val)) {
      return String(+val * 100);
    }

    return val;
  };

  const handleOnPurchase = () => {
    const { isContributorAnotherPerson, ...formData } = values;

    getPaymentKey({
      ...paymentKeyPayload,
      totalPaid: handleParsePrice(paymentKeyPayload.totalPaid),
      totalProjectPrice: handleParsePrice(paymentKeyPayload.totalProjectPrice),
      totalSustainablePrice: handleParsePrice(paymentKeyPayload.totalSustainablePrice),
      billingDetails: { ...formData, phoneNo: String(formData.phoneNo) },
    });
  };

  return {
    isConfirmPopupOpen,
    isLoadingPayment: isLoadingPaymentKey || isLoadingPayment,
    errors,
    serverError,
    setErrors,
    handleOnPurchase,
    setIsConfirmPopupOpen,
  };
};

export default usePaymentForm;
